(() => {
  /* =========================================================
     0. LIVE CART COUNT
     ---------------------------------------------------------
     - Hidden while the cart is empty (also on page load).
     - Appears / goes up the moment anything is added — no refresh.
     - Works with any product form, quick-add or app, because it
       watches the store's own cart requests (/cart/add, /cart/change,
       /cart/update, /cart/clear) instead of relying on one theme's code.
     ========================================================= */
  if (!window.__pawRoamCartWatch) {
    window.__pawRoamCartWatch = true;

    const CART_WRITE = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/;

    const cartJsUrl = () => {
      const header = document.querySelector(".paw-roam-header[data-cart-url]");
      const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || "/";
      return ((header && header.dataset.cartUrl) || root + "cart") + ".js";
    };

    const setCartCount = (value) => {
      const count = parseInt(value, 10) || 0;

      document.querySelectorAll("[data-pr-cart-count]").forEach((badge) => {
        const previous = parseInt(badge.textContent, 10) || 0;
        badge.textContent = count > 99 ? "99+" : String(count);
        badge.hidden = count === 0;

        if (count > previous && badge.closest(".paw-roam-header__cart-link")) {
          badge.classList.remove("is-bumped");
          void badge.offsetWidth; // restart the animation
          badge.classList.add("is-bumped");
        }
      });

      document.querySelectorAll("[data-pr-cart-count-wrap]").forEach((wrap) => {
        wrap.hidden = count === 0;
      });

      document.querySelectorAll(".paw-roam-header__cart-link[data-pr-cart-link]").forEach((link) => {
        link.setAttribute("aria-label", `Cart, ${count} ${count === 1 ? "item" : "items"}`);
      });
    };

    let refreshTimer = null;
    const refreshCartCount = () => {
      // Several cart requests in a row (e.g. bundles) → one refresh
      clearTimeout(refreshTimer);
      refreshTimer = setTimeout(() => {
        nativeFetch(cartJsUrl(), { headers: { Accept: "application/json" }, cache: "no-store" })
          .then((response) => (response.ok ? response.json() : null))
          .then((cart) => {
            if (cart && typeof cart.item_count !== "undefined") setCartCount(cart.item_count);
          })
          .catch(() => {});
      }, 60);
    };

    const urlOf = (input) => {
      if (typeof input === "string") return input;
      if (input && input.url) return input.url;
      return String(input || "");
    };

    /* Watch fetch() — used by most themes and apps */
    const nativeFetch = window.fetch.bind(window);
    window.fetch = function (input, init) {
      const promise = nativeFetch(input, init);
      if (CART_WRITE.test(urlOf(input))) {
        promise
          .then((response) => {
            if (response && response.ok) refreshCartCount();
          })
          .catch(() => {});
      }
      return promise;
    };

    /* Watch XMLHttpRequest — used by older / jQuery-based add-to-cart */
    const nativeOpen = XMLHttpRequest.prototype.open;
    const nativeSend = XMLHttpRequest.prototype.send;
    XMLHttpRequest.prototype.open = function (method, url) {
      this.__prCartWrite = CART_WRITE.test(String(url || ""));
      return nativeOpen.apply(this, arguments);
    };
    XMLHttpRequest.prototype.send = function () {
      if (this.__prCartWrite) {
        this.addEventListener("load", () => {
          if (this.status >= 200 && this.status < 300) refreshCartCount();
        });
      }
      return nativeSend.apply(this, arguments);
    };

    /* Custom events other scripts may send */
    ["cart:updated", "cart:refresh", "cart:change", "paw:cart-updated"].forEach((name) => {
      document.addEventListener(name, (event) => {
        const cart = event.detail && event.detail.cart;
        if (cart && typeof cart.item_count !== "undefined") {
          setCartCount(cart.item_count);
        } else {
          refreshCartCount();
        }
      });
    });

    /* Back / forward button can show a cached page with an old number */
    window.addEventListener("pageshow", (event) => {
      if (event.persisted) refreshCartCount();
    });

    window.PawRoam = window.PawRoam || {};
    window.PawRoam.setCartCount = setCartCount;
    window.PawRoam.refreshCartCount = refreshCartCount;
  }

  const initHeader = () => {
    const headers = document.querySelectorAll(".paw-roam-header");

    if (!headers.length) return;

    /* =========================================================
       1. SCROLL BLUR DETECTION
       ========================================================= */
    const updateHeaderState = () => {
      const isScrolled = window.scrollY > 20;
      headers.forEach((header) => {
        header.classList.toggle("is-scrolled", isScrolled);
      });
    };

    updateHeaderState();
    window.addEventListener("scroll", updateHeaderState, { passive: true });

    /* =========================================================
       2. MOBILE MENU & ACCORDION SUBMENU TOGGLE
       ========================================================= */
    headers.forEach((header) => {
      if (header.dataset.initialized === "true") return;
      header.dataset.initialized = "true";

      const toggle = header.querySelector(".paw-roam-header__toggle");
      const mobileMenu = header.querySelector(".paw-roam-header__mobile");

      if (!toggle || !mobileMenu) return;

      const openMenu = () => {
        mobileMenu.hidden = false;
        toggle.setAttribute("aria-expanded", "true");
        toggle.setAttribute("aria-label", "Close menu");
      };

      const closeMenu = () => {
        mobileMenu.hidden = true;
        toggle.setAttribute("aria-expanded", "false");
        toggle.setAttribute("aria-label", "Open menu");
      };

      toggle.addEventListener("click", (e) => {
        e.stopPropagation();
        const isOpen = toggle.getAttribute("aria-expanded") === "true";
        if (isOpen) {
          closeMenu();
        } else {
          openMenu();
        }
      });

      // Accordion Submenu for Mobile
      const accordionToggles = header.querySelectorAll(".paw-roam-header__mobile-accordion-toggle");
      accordionToggles.forEach((btn) => {
        btn.addEventListener("click", (e) => {
          e.preventDefault();
          const item = btn.closest(".paw-roam-header__mobile-item");
          const submenu = item.querySelector(".paw-roam-header__mobile-submenu");
          if (submenu) {
            const isHidden = submenu.hidden;
            submenu.hidden = !isHidden;
            btn.classList.toggle("is-open", isHidden);
          }
        });
      });

      document.addEventListener("click", (event) => {
        if (!header.contains(event.target) && !mobileMenu.hidden) {
          closeMenu();
        }
      });

      document.addEventListener("keydown", (event) => {
        if (event.key === "Escape") closeMenu();
      });
    });
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initHeader);
  } else {
    initHeader();
  }
})();
